import React, { createContext, useEffect, useState } from 'react';
import { noop } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { api as collectionsAPI } from '@fiverr-private/collect';
import { logError } from '../../../../lib/logger';
import type { Collection } from '../../../../lib/types/collections';

/**
 * Collections data.
 * `null` means the data is not loaded yet
 */
type CollectionsData = Collection[] | null;
type SetCollectionsCallback = (collections: Collection[]) => void;
type CollectionsContextValue = [CollectionsData, SetCollectionsCallback];

const initialValue = [null, noop] satisfies CollectionsContextValue;

export const CollectionsContext = createContext<CollectionsContextValue>(initialValue);

interface CollectionsContextProviderProps {
  children: React.ReactNode;
}

export const CollectionsContextProvider = ({ children }: CollectionsContextProviderProps) => {
  const [collections, setCollections] = useState<CollectionsData>(null);
  const loaded = collections !== null;

  const isLoggedInUser = Boolean(getContext().userId);

  useEffect(() => {
    if (loaded) {
      return;
    }

    if (!isLoggedInUser) {
      setCollections([]); // don't fetch collections data if user is guest
      return;
    }

    const fetchCollections = async () => {
      try {
        const { collections } = await collectionsAPI.getCollections();
        setCollections(collections as Collection[]);
      } catch (error) {
        logError('CollectionsContextProvider: Failed to fetch collections from collectionsAPI', {
          error,
        });
        setCollections([]);
      }
    };

    fetchCollections();
  }, [loaded, isLoggedInUser, setCollections]);

  return <CollectionsContext.Provider value={[collections, setCollections]}>{children}</CollectionsContext.Provider>;
};
